import type {
  BalanceRegistration,
  BalanceSlot,
  BalanceSlotStatus,
} from '@sportninja/common/services/teamBalance'
import { balanceLabel } from './balanceI18n'
import { isOpenPlayerSlot } from './balanceRowKind'

/**
 * SN-4709 × SN-8081 (D47) — the CLIENT-side display state for a balance slot.
 *
 * The server's five-value status enum is deliberately unchanged: it drives the
 * grid's sort ranks, the `BalanceSlotStatus` union and existing tests. PLAYER
 * FINDER is therefore a display variant layered on top of `open`, derived here.
 *
 * THIS MODULE IS THE SINGLE SOURCE OF THAT DERIVATION. The chip, the grid
 * `valueGetter`, the filter and the sort comparator all read it — two code paths
 * computing the displayed value is exactly how sorting silently starts
 * disagreeing with what the cell shows.
 */
export type BalanceSlotDisplayStatus =
  | BalanceSlotStatus
  | 'player_finder'
  | 'player_finder_hidden'

/**
 * Is this slot currently advertised in the Player Finder?
 *
 * Guarded on "still an open PLAYER slot" as well as the flag: the server clears
 * `finder_listed` when a player claims the slot, but a payload rendered mid-flight
 * (or an older api2 that does not send the field at all) must never show an ad on
 * an occupied row — nor on a team official's line (Sep 23), which is never a spot a
 * player can take.
 */
export const isSlotListedInFinder = (slot: BalanceSlot): boolean =>
  slot.finder_listed === true && isOpenPlayerSlot(slot)

/**
 * Is this listed slot's ad actually RUNNING?
 *
 * Listing is per slot, but a listed slot is only advertised while Player Finder
 * search shows the team's ad: the team's recruiting switch can be off (it never
 * unticks slots), the competition can turn Player Finder off, or the roster can
 * be full. The server answers with `finder_advertised`, using search's own rules;
 * an older api2 that does not send it had none of these, so a listed slot was
 * advertised.
 */
export const isSlotAdvertisedInFinder = (slot: BalanceSlot): boolean =>
  isSlotListedInFinder(slot) && (slot.finder_advertised ?? true)

/** Listed, but players cannot see its ad — `finderHiddenCause` says why. */
export const isSlotListedButHidden = (slot: BalanceSlot): boolean =>
  isSlotListedInFinder(slot) && !isSlotAdvertisedInFinder(slot)

/**
 * Review round 1, TF-03 — the ONE reason the sheet gives for a hidden ad.
 *
 * - `finder_disabled` — the competition turned Player Finder off. Named first:
 *   nothing the team does brings the ad back meanwhile (the server refuses to
 *   turn recruiting on), so any other advice would send the rep the wrong way.
 * - `registration_private` — SN-4709: the team is Private, and private means
 *   unlisted. The team's own deliberate choice, so it is named before the
 *   recruiting switch; its copy says other reasons may remain once it is public.
 * - `recruiting_off` — the team's own switch, changed in Player Finder settings.
 * - `roster_full` — search hides a team with no room for another player.
 * - `other` — nothing this build can explain; the settings sub-tab lists the
 *   server's reasons.
 */
export type FinderHiddenCause =
  | 'finder_disabled'
  | 'registration_private'
  | 'recruiting_off'
  | 'roster_full'
  | 'other'

const HIDDEN_CAUSE_PRECEDENCE: FinderHiddenCause[] = [
  'finder_disabled',
  'registration_private',
  'recruiting_off',
  'roster_full',
]

/**
 * From the server's `player_finder_hidden_reasons`. An older api2 sends none; its
 * only way to hide a listed slot was the recruiting switch.
 */
export const finderHiddenCause = (
  registration: Pick<
    BalanceRegistration,
    'player_finder_hidden_reasons' | 'player_finder_recruiting'
  >
): FinderHiddenCause => {
  const reasons = registration.player_finder_hidden_reasons
  if (reasons === undefined) {
    return registration.player_finder_recruiting === false
      ? 'recruiting_off'
      : 'other'
  }

  return (
    HIDDEN_CAUSE_PRECEDENCE.find((cause) => reasons.includes(cause)) ?? 'other'
  )
}

/**
 * The display status the chip, sort and filter all agree on.
 *
 * PLAYER FINDER only while the ad actually runs — the sheet must never claim an
 * ad that players cannot see. A listed slot whose ad is hidden reads as its own
 * muted state instead of falling back to OPEN, so the rep can still see which
 * spots will be advertised again once the ad can run.
 */
export const slotDisplayStatus = (
  slot: BalanceSlot
): BalanceSlotDisplayStatus => {
  if (isSlotAdvertisedInFinder(slot)) {
    return 'player_finder'
  }

  return isSlotListedButHidden(slot) ? 'player_finder_hidden' : slot.status
}

/**
 * The POSITION column's value.
 *
 * Two sources, never mixed: an OCCUPIED slot shows its occupant's roster
 * position, a LISTED OPEN slot shows the position it is recruiting for. An
 * ordinary open slot has neither and yields '' so those rows group at one end of
 * a sort rather than scattering.
 */
export const slotPositionLabel = (slot: BalanceSlot): string => {
  const position = isSlotListedInFinder(slot)
    ? slot.finder_position
    : slot.player?.position

  return position?.name_full || position?.name || position?.abbreviation || ''
}

/**
 * Lifecycle order for sorting, matching the server enum's ranks with the finder
 * variants slotted beside `open` — a listed slot IS an open slot, so it belongs
 * next to one rather than at either extreme. The hidden listing sits between the
 * two: listed, but not yet doing what PLAYER FINDER does.
 */
export const DISPLAY_STATUS_RANK: Record<BalanceSlotDisplayStatus, number> = {
  open: 0,
  player_finder_hidden: 1,
  player_finder: 2,
  pending: 3,
  confirmed: 4,
  paid: 5,
  cancelled: 6,
}

/**
 * Every display status in lifecycle order — derived from the ranks above rather
 * than re-listed, so the STATUS header's legend can never fall out of step with
 * the sort it is describing (or miss a status added later).
 */
export const DISPLAY_STATUS_ORDER = (
  Object.keys(DISPLAY_STATUS_RANK) as BalanceSlotDisplayStatus[]
).sort((a, b) => DISPLAY_STATUS_RANK[a] - DISPLAY_STATUS_RANK[b])

/**
 * The human label for every display status.
 *
 * SN-4709 (James, Jul 31) — CONFIRMED reads INVOICED. The server enum is
 * deliberately untouched: its five values are load-bearing for the grid sort
 * ranks, the `BalanceSlotStatus` union and the api2 test suite (D47 set that
 * precedent for `player_finder`). The rename is display-only, and it lives HERE
 * because this one function feeds the chip, the grid `valueGetter`, the text
 * filter and the sort comparator — so the label can never drift between what a
 * row says and what it filters or sorts as.
 *
 * The other four move to translated copy at the same time. They used to render
 * the RAW server value ("cancelled"), which was untranslatable and lower-case in
 * a column of Title Case labels.
 */
const STATUS_LABEL_KEYS: Record<BalanceSlotDisplayStatus, string> = {
  open: 'common:balanceSlotStatusOpen',
  player_finder: 'common:balanceStatusPlayerFinder',
  player_finder_hidden: 'common:balanceStatusPlayerFinderHidden',
  pending: 'common:balanceSlotStatusPending',
  // "Invoiced", not "Confirmed" — the state IS "a payment request went out and a
  // balance is still due", which is what lockSlot() actually does.
  confirmed: 'common:balanceSlotStatusInvoiced',
  paid: 'common:balanceSlotStatusPaid',
  cancelled: 'common:balanceSlotStatusCancelled',
}

export const displayStatusLabel = (status: BalanceSlotDisplayStatus): string =>
  balanceLabel(STATUS_LABEL_KEYS[status] ?? STATUS_LABEL_KEYS.open)

/**
 * Lifecycle rank for a slot, derived from the SAME display status the chip and
 * the valueGetter use.
 *
 * Deliberately keyed on the slot rather than on the rendered label: the column's
 * sort value is the translated label (so the text filter matches what is on
 * screen), and reverse-mapping a label back to a status would collapse the
 * moment two labels coincided — which they do the instant a translation is
 * missing and both resolve to ''. Ranking from the row data cannot drift.
 */
export const slotDisplayRank = (slot: BalanceSlot): number =>
  DISPLAY_STATUS_RANK[slotDisplayStatus(slot)] ?? -1
